"use client";

import { useEffect, useRef, useState } from "react";
import { InvitationData } from "./types";
import { toast } from "react-toastify";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { Figtree } from "next/font/google";
import styles from "./page.module.css";
import { MdErrorOutline } from "react-icons/md";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { env } from "@/lib/env";
import { ApiError } from "@/types/types";

const figtree = Figtree({ weight: ["400", "500", "600"], subsets: ["latin"] });
const AGENCYENDPOINTURL = env.NEXT_AGENCY_API_URL;

export default function AcceptInvitationLayout({
  children,
  token,
}: {
  children: React.ReactNode;
  token: string;
}) {
  const locale = useLocale();
  const router = useRouter();
  const { userData } = useUserAuthDataContext();
  const [isNotAuthenticated, setIsNotAuthenticated] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(true);
  const isLoading = useRef(false);
  const t = useTranslations("InvitationPage");
  useEffect(() => {
    if (userData.sessionId == null) {
      toast(t("pleaseReauth"), {
        type: "error",
      });
      setIsNotAuthenticated(true);
      const url = new URL(window.location.href);
      url.searchParams.set("signIn", "true");

      const newUrl =
        url.pathname + (url.search ? `?${url.searchParams.toString()}` : "");

      router.push(newUrl);
    } else {
      setRetry(true);
      setIsNotAuthenticated(false);
    }
  }, [userData.sessionId]);

  useEffect(() => {
    if (!userData.sessionId || isLoading.current) return;

    if (!retry) return;
    //we call endpoint
    const acceptInvitation = async () => {
      isLoading.current = true;
      setFailed(false);
      setIsConfirming(true);
      try {
        const response = await fetch(
          `${AGENCYENDPOINTURL}/acceptInvitation?token=${token}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: userData.idToken!,
            },
            body: JSON.stringify({
              token: token,
            }),
          },
        );
        if (!response.ok) {
          const apiErr = (await response.json()) as ApiError;
          throw Error(apiErr.message);
        }

        toast.success(t("welcomeToAgency"));
        router.push(`/${locale}/dashboard`);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : t("unexpectedError");
        setFailed(true);
        toast.error(errorMessage);
      } finally {
        isLoading.current = false;
        setRetry(false);
        setIsConfirming(false);
      }
    };

    acceptInvitation();
  }, [userData.sessionId, retry, token, locale, router]);

  return (
    <main
      className={`${figtree.className} ${styles.main}`}
      role="main"
      aria-label={t("ariaMainInvitation")}
    >
      <div className={styles.card}>
        {children}

        {/* Action / Status Area */}
        <div className={styles.actionArea}>
          {isNotAuthenticated && (
            <div className={styles.errorBox} role="alert" aria-live="assertive">
              <MdErrorOutline className={styles.errorIcon} aria-hidden="true" />
              <p className={styles.errorMessageText}>{t("pleaseReauth")}</p>
            </div>
          )}

          {isConfirming && (
            <div className={styles.loadingWrapper}>
              <div className={styles.spinner}></div>
              <p
                className={styles.status}
                role="status"
                aria-live="polite"
                aria-label={t("ariaConfirming")}
              >
                {t("confirmingAccept")}
              </p>
            </div>
          )}

          {failed && (
            <button
              className={styles.retryButton}
              onClick={(e) => {
                e.preventDefault();
                setRetry((prev) => !prev);
              }}
              aria-label={t("ariaRetryButton")}
            >
              {t("retry")}
            </button>
          )}
        </div>
      </div>
    </main>
  );
}
